/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import {
  DataGrid,
  GridToolbar,
  GridToolbarQuickFilter,
  GridPagination,
} from "@mui/x-data-grid";
import { Grid } from "../Grid";
import { DataTableProps } from "./IDataTable";

export default function DataTable(props: DataTableProps) {
  const {
    rows,
    columns,
    loading = false,
    type = "compact",
    footerToolbar = <></>,
    disableColumnResize = false,
    checkboxSelection = false,
    pageSize = 20,
    pageSizeOptions = [1, 10, 20, 50],
    rowHeight,
    headerRowHeight,
    headerLineHeight,
    showToolbar = false,
    ...rest
  } = props;

  // Ensure current pageSize is in pageSizeOptions
  const finalPageSizeOptions = Array.from(new Set([...pageSizeOptions, pageSize])).sort((a, b) => a - b);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <DataGrid
        {...rest}
        rows={rows}
        columns={columns}
        pageSizeOptions={finalPageSizeOptions}
        rowHeight={rowHeight}
        columnHeaderHeight={headerRowHeight}
        disableColumnResize={disableColumnResize}
        density={type}
        checkboxSelection={checkboxSelection}
        loading={loading}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        localeText={{
          noRowsLabel: 'No records found',
        }}
        initialState={{
          pagination: { paginationModel: { pageSize } },
        }}
        sx={(theme) => ({
          borderColor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[700]
              : theme.palette.grey[200],
          "& .MuiDataGrid-cell": {
            borderColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[700]
                : theme.palette.grey[200],
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            lineHeight: headerLineHeight,
            whiteSpace: "normal",
            textAlign: "inherit",
          },
          "& .MuiDataGrid-columnHeaders": {
            minHeight: headerRowHeight,
            maxHeight: headerRowHeight,
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: `2px solid ${theme.palette.primary.main} !important`,
            outlineOffset: "-2px",
          },
          "& .MuiDataGrid-columnHeader:focus-within": {
            outline: `2px solid ${theme.palette.primary.main} !important`,
            outlineOffset: "-2px",
          },
          "& .MuiTablePagination-select:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiTablePagination-select:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main} !important`,
            outlineOffset: "-2px",
          },
          "& .MuiTablePagination-actions :hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiTablePagination-actions button:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main} !important`,
            outlineOffset: "2px",
          },
        })}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: { variant: "outlined", size: "small" },
              columnInputProps: { variant: "outlined", size: "small", sx: { mt: "auto" } },
              operatorInputProps: { variant: "outlined", size: "small", sx: { mt: "auto" } },
              valueInputProps: { InputComponentProps: { variant: "outlined", size: "small" } },
            },
          },
        }}
        slots={{
          toolbar: showToolbar
            ? () => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.5rem 1rem",
                }}
              >
                {/* Left: Buttons (columns, filters, density, export) */}
                <div style={{ flex: "0 0 auto", display: "flex", gap: 8 }}>
                  <GridToolbar />
                </div>

                {/* Right: Quick Filter search */}
                <div style={{ flex: "0 0 auto" }}>
                  <GridToolbarQuickFilter debounceMs={300} />
                </div>
              </div>
            )
            : undefined,
          pagination: () => (
            <Grid container width="100%" height="auto" direction="row" sx={{ alignItems: "center" }}>
              {footerToolbar}
              <GridPagination />
            </Grid>
          ),
        }}
      />


    </div>
  );
}
